import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ShiftNoteAttachments } from "@/components/ShiftNoteAttachments";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RichTextEditor, sanitizeHtml } from "@/components/RichTextEditor";
import {
  IdCard, Search, Loader2, Save, ShieldCheck, GraduationCap, StickyNote, FileText,
  ShieldAlert, Mail, Phone, MapPin, Languages, Heart, User, Trash2, Plus, Lock, CalendarClock, Eye, ChevronRight,
  FolderLock, FolderOpen, CheckCircle2,
} from "lucide-react";
import { fmtLog } from "@/lib/tz";

const TEAL = "#2f6f6b";

const STATUS_TONE = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-slate-100 text-slate-500 border-slate-200",
  Suspended: "bg-amber-50 text-amber-700 border-amber-200",
  Left: "bg-rose-50 text-rose-700 border-rose-200",
};
const SEV_TONE = {
  Low: "bg-slate-100 text-slate-600 border-slate-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Critical: "bg-rose-50 text-rose-700 border-rose-200",
};
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");
const fmtDT = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

export default function StaffProfiles() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [sel, setSel] = useState(searchParams.get("open") || null);

  useEffect(() => { api.staffProfiles().then(setRows).catch(() => { setRows([]); toast.error("Could not load staff profiles."); }); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (rows || []).filter((r) => !t || [r.display_name, r.name, r.primary_role, r.company_email].some((v) => String(v || "").toLowerCase().includes(t)));
  }, [rows, q]);

  const pick = (name) => { setSel(name); const p = new URLSearchParams(searchParams); if (name) p.set("open", name); else p.delete("open"); setSearchParams(p, { replace: true }); };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="staff-profiles-page">
      <div className="flex items-center gap-2.5">
        <IdCard className="h-5 w-5" style={{ color: TEAL }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Staff Profiles</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-4">
        Read-only staff profiles synced from HR. Rostering considerations sync both ways with HR; office notes stay here only.
      </p>

      <div className="grid lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-4">
        <div>
          <div className="relative mb-3">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" className="h-9 pl-9 text-sm bg-white" data-testid="staff-search" />
          </div>
          {rows === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: TEAL }} /></div>
          ) : (
            <div className="space-y-1.5 max-h-[74vh] overflow-y-auto pr-1" data-testid="staff-list">
              {filtered.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">No staff found.</div>}
              {filtered.map((r) => (
                <button key={r.name} onClick={() => pick(r.name)} data-testid={`staff-row-${r.name}`}
                  className={`w-full text-left rounded-lg border p-3 transition-all ${sel === r.name ? "border-[#2f6f6b] bg-teal-50/40" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div className="flex items-center gap-2.5">
                    {r.photo
                      ? <img src={r.photo} alt={r.display_name || r.name} className="h-9 w-9 rounded-full object-cover shrink-0" data-testid={`staff-row-photo-${r.name}`} />
                      : <div className="h-9 w-9 rounded-full bg-slate-700 text-white flex items-center justify-center text-[11px] font-semibold shrink-0">{(r.display_name || r.name).split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{r.display_name || r.name}</span>
                        <Badge className={`text-[9px] py-0 shrink-0 border ${STATUS_TONE[r.status] || STATUS_TONE.Inactive}`}>{r.status || "—"}</Badge>
                      </div>
                      <div className="text-[11px] text-slate-400 truncate">{[r.primary_role, r.company_email].filter(Boolean).join(" · ") || r.name}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {!sel ? (
            <div className="rounded-xl border border-dashed border-slate-200 h-full min-h-[300px] flex items-center justify-center text-sm text-slate-400">
              Select a staff member to view their profile.
            </div>
          ) : (
            <ProfileDetail key={sel} name={sel} onStatus={(name, status) => setRows((rs) => (rs || []).map((r) => r.name === name ? { ...r, status } : r))} />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      {Icon && <Icon className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-sm text-slate-700 break-words">{value || <span className="text-slate-300">—</span>}</div>
      </div>
    </div>
  );
}

function DocTable({ rows, testid, profileName }) {
  if (!rows || rows.length === 0) return <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">None on file.</div>;
  const openDoc = async (url) => {
    try {
      const blob = await api.staffProfileDocument(profileName, url);
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    } catch { toast.error("Could not open document."); }
  };
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <th className="px-3 py-2 font-semibold">Document</th><th className="px-3 py-2 font-semibold">Reference</th><th className="px-3 py-2 font-semibold">Expiry</th><th className="px-3 py-2 font-semibold w-20 text-right">File</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-slate-50/60">
              <td className="px-3 py-2 text-slate-700">{r.document_type || "—"}</td>
              <td className="px-3 py-2 text-slate-500">{r.reference_number || "—"}</td>
              <td className="px-3 py-2 text-slate-500">{fmtDate(r.expiry_date)}</td>
              <td className="px-3 py-2 text-right">
                {r.document_file ? (
                  <button onClick={() => openDoc(r.document_file)} className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 hover:text-teal-900 hover:underline" data-testid={`doc-view-${i}`}>
                    <Eye className="h-3.5 w-3.5" />View
                  </button>
                ) : <span className="text-slate-300 text-xs">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProfileDetail({ name, onStatus }) {
  const { can, user } = useAuth();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("profile");
  const [rc, setRc] = useState("");
  const [savingRc, setSavingRc] = useState(false);

  useEffect(() => {
    api.staffProfile(name).then((d) => { setP(d); setRc(d.rostering_considerations || ""); onStatus && onStatus(name, d.status); }).catch(() => toast.error("Could not load profile."));
  }, [name]);

  const saveRc = async () => {
    setSavingRc(true);
    try { await api.staffProfileRostering(name, rc); toast.success("Rostering considerations saved & synced to HR."); setP((x) => ({ ...x, rostering_considerations: rc })); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSavingRc(false); }
  };

  if (!p) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: TEAL }} /></div>;
  const dirty = (rc || "") !== (p.rostering_considerations || "");
  // Your own profile: basic details only without hr.own_record (the server enforces it).
  const own = !!p.own_record;

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="staff-detail">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {p.photo
            ? <img src={p.photo} alt={p.display_name || p.name} className="h-12 w-12 rounded-full object-cover shrink-0" data-testid="staff-detail-photo" />
            : <div className="h-12 w-12 rounded-full bg-slate-700 text-white flex items-center justify-center text-sm font-semibold shrink-0">{(p.display_name || p.name).split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>}
          <div className="min-w-0">
            <div className="text-lg font-semibold font-brand text-slate-900 truncate">{p.display_name || p.name}</div>
            <div className="text-xs text-slate-500 truncate">{[p.primary_role, p.name].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <Badge className={`shrink-0 border ${STATUS_TONE[p.status] || STATUS_TONE.Inactive}`}>{p.status || "—"}</Badge>
      </div>

      {own && (
        <div className="mx-4 mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="staff-own-record">
          This is your own staff profile, so you can see its basic details only.
        </div>
      )}
      <Tabs value={own && ["office", "incidents"].includes(tab) ? "profile" : tab} onValueChange={setTab} className="p-4">
        <TabsList data-testid="staff-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="h-3.5 w-3.5 mr-1.5" />Profile</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Compliance</TabsTrigger>
          {!own && <TabsTrigger value="office" data-testid="tab-office-notes"><StickyNote className="h-3.5 w-3.5 mr-1.5" />Office Notes</TabsTrigger>}
          <TabsTrigger value="shiftnotes" data-testid="tab-shift-notes"><FileText className="h-3.5 w-3.5 mr-1.5" />Shift Notes</TabsTrigger>
          {!own && <TabsTrigger value="incidents" data-testid="tab-incidents"><ShieldAlert className="h-3.5 w-3.5 mr-1.5" />Incidents</TabsTrigger>}
          <TabsTrigger value="docaccess" data-testid="tab-doc-access"><Eye className="h-3.5 w-3.5 mr-1.5" />Document Access</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-5">
          <div className="grid sm:grid-cols-2 gap-x-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Personal</div>
              <Row icon={User} label="Preferred name" value={p.preferred_name} />
              <Row label="Legal name" value={[p.first_name, p.last_name].filter(Boolean).join(" ") || p.employee_name} />
              <Row label="Pronouns" value={p.pronouns} />
              <Row label="Gender" value={p.gender} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Contact</div>
              <Row icon={Phone} label="Mobile" value={p.cell_number} />
              <Row icon={Mail} label="Email" value={p.company_email} />
              <Row icon={MapPin} label="Address" value={p.address} />
              <Row icon={Languages} label="Languages" value={p.languages} />
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><ShieldAlert className="h-3 w-3" />Emergency contact</div>
            <div className="grid sm:grid-cols-3 gap-x-6">
              <Row label="Name" value={p.emer_name} />
              <Row label="Phone" value={p.emer_phone} />
              <Row label="Relation" value={p.emer_relation} />
            </div>
          </div>

          {(p.hobbies || p.about_me) && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5"><Heart className="h-3 w-3" />Hobbies & interests</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{p.hobbies || <span className="text-slate-300">—</span>}</div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">About me</div>
                <div className="text-sm text-slate-700 prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.about_me) || "<span class='text-slate-300'>—</span>" }} />
              </div>
            </div>
          )}

          {!own && <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3" data-testid="rostering-block">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-teal-800 flex items-center gap-1.5"><CalendarClock className="h-3 w-3" />Rostering considerations <span className="font-medium text-teal-600 normal-case">· editable, syncs with HR</span></div>
            </div>
            <div className="bg-white rounded-md">
              <RichTextEditor value={rc} onChange={setRc} disabled={!can.has("staff_profiles.edit")} testid="rostering-editor" placeholder="Add rostering considerations…" />
            </div>
            {can.has("staff_profiles.edit") && <div className="flex justify-end mt-2">
              <Button size="sm" style={{ backgroundColor: TEAL }} onClick={saveRc} disabled={savingRc || !dirty} data-testid="rostering-save">
                {savingRc ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save & sync to HR
              </Button>
            </div>}
          </div>}
        </TabsContent>

        <TabsContent value="compliance" className="mt-4 space-y-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" />Active clearances & checks</div>
            <DocTable rows={p.active_compliance} testid="compliance-table" profileName={name} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><GraduationCap className="h-3 w-3" />Skills & training</div>
            <DocTable rows={p.active_skills} testid="skills-table" profileName={name} />
          </div>
          <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Lock className="h-3 w-3" />Compliance is read-only here — it's managed in HR and synced across automatically.</p>
        </TabsContent>

        {!own && <TabsContent value="office" className="mt-4"><OfficeNotes name={name} can={can} user={user} /></TabsContent>}
        <TabsContent value="shiftnotes" className="mt-4"><ShiftNotesList name={name} /></TabsContent>
        {!own && <TabsContent value="incidents" className="mt-4"><IncidentsList name={name} canOpen={can.has("incidents.view")} /></TabsContent>}
        <TabsContent value="docaccess" className="mt-4"><DocAccessList name={name} /></TabsContent>
      </Tabs>
    </div>
  );
}

function OfficeNotes({ name, can, user }) {
  const [notes, setNotes] = useState(null);
  const [types, setTypes] = useState([]);
  const [subject, setSubject] = useState("");
  const [noteType, setNoteType] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.staffOfficeNotes(name).then(setNotes).catch(() => setNotes([]));
  useEffect(() => { load(); }, [name]);
  useEffect(() => {
    api.officeNoteTypes().then((t) => { setTypes(t); if (t.length) setNoteType((cur) => cur || t[0].label); }).catch(() => setTypes([]));
  }, []);

  const add = async () => {
    if (!subject.trim()) { toast.error("Subject is required."); return; }
    if (!note.trim()) { toast.error("Enter a note."); return; }
    setBusy(true);
    try { await api.staffOfficeNoteAdd(name, { subject, note, note_type: noteType }); setSubject(""); setNote(""); setNoteType(types[0]?.label || ""); toast.success("Office note added."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add note."); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this office note?")) return;
    try { await api.staffOfficeNoteDelete(name, id); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
  };

  return (
    <div>
      {can.has("staff_profiles.edit") && <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 mb-4" data-testid="office-note-form">
        <div className="flex gap-2 mb-2">
          <Select value={noteType} onValueChange={setNoteType}>
            <SelectTrigger className="h-9 text-sm bg-white w-40" data-testid="office-note-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.label} className="text-sm">{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (required)" className="h-9 text-sm bg-white flex-1" data-testid="office-note-subject" />
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an office note (visible to office staff, not synced to HR)…" className="text-sm bg-white" rows={3} data-testid="office-note-input" />
        <div className="flex justify-end mt-2">
          <Button size="sm" style={{ backgroundColor: TEAL }} onClick={add} disabled={busy} data-testid="office-note-add">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Add note
          </Button>
        </div>
      </div>}

      {notes === null ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : notes.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No office notes yet.</div>
      ) : (
        <div className="space-y-2" data-testid="office-notes-list">
          {notes.map((n) => {
            const canDel = can.has("staff_profiles.notes_delete_any")
              || (can.has("staff_profiles.edit") && (n.author_name === user?.name || n.author === user?.username));
            return (
              <div key={n.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`office-note-${n.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {n.note_type && <Badge className="bg-teal-50 text-teal-700 border-teal-200 text-[9px] py-0 shrink-0">{n.note_type}</Badge>}
                    {n.subject && <span className="text-sm font-medium text-slate-800 truncate">{n.subject}</span>}
                  </div>
                  {canDel && <button onClick={() => del(n.id)} className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 shrink-0" title="Delete" data-testid={`office-note-del-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{n.note}</div>
                <div className="text-[10px] text-slate-400 mt-1.5">{n.author_name || n.author} · {fmtLog(n.created_at)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ShiftNotesList({ name }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.staffProfileShiftNotes(name).then(setRows).catch(() => setRows([])); }, [name]);
  if (rows === null) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (rows.length === 0) return <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No shift notes submitted.</div>;
  const Field = ({ label, value }) => value ? (
    <div className="mt-1.5"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}: </span><span className="text-sm text-slate-600 whitespace-pre-wrap">{value}</span></div>
  ) : null;
  return (
    <div className="space-y-2" data-testid="shift-notes-list">
      {rows.map((r) => {
        const empty = !r.events && !r.mood && !r.urgent_notes;
        return (
          <div key={r.name} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`shift-note-${r.name}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-medium text-slate-800">{r.participant_name || "—"}</span>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[9px] py-0">{r.status || "—"}</Badge>
                <span className="text-[11px] text-slate-400">{fmtDate(r.shift_date)}</span>
              </div>
            </div>
            {empty ? <div className="text-sm text-slate-300">No note text.</div> : (
              <>
                <Field label="Events" value={r.events} />
                <Field label="Mood" value={r.mood} />
                <Field label="Urgent notes" value={r.urgent_notes} />
                {r.contacted_office ? <Field label="Contacted office" value={r.office_contact_reason || "Yes"} /> : null}
              </>
            )}
            {(r.attachments || []).length > 0 && (
              <div className="mt-2"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Attachments</span>
                <div className="mt-1"><ShiftNoteAttachments items={r.attachments} load={(idx, preview) => api.shiftNoteAttachment(r.name, idx, preview)} /></div></div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DocAccessList({ name }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.staffDocumentAccess(name).then(setRows).catch(() => setRows([])); }, [name]);
  if (rows === null) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (rows.length === 0) return <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg" data-testid="doc-access-empty">This worker hasn't opened any participant documents yet.</div>;
  return (
    <div className="space-y-2" data-testid="doc-access-list">
      <p className="text-xs text-slate-500 mb-1">Every participant document this worker has opened in the worker app, with total views, last opened, and read-acknowledgement.</p>
      {rows.map((r) => (
        <div key={`${r.kind}:${r.doc_id}`} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`doc-access-${r.kind}-${r.doc_id}`}>
          {r.kind === "external"
            ? <FolderOpen className="h-4 w-4 text-sky-500 shrink-0" title="External document" />
            : <FolderLock className="h-4 w-4 text-emerald-600 shrink-0" title="Internal document" />}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">
              {r.doc_title || "Document"}
              {r.acknowledged && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 font-medium" title={`Acknowledged ${fmtLog(r.acknowledged_at)}`}>
                  <CheckCircle2 className="h-3 w-3" /> read
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500">
              {r.participant_name || r.participant}
              {" · "}
              <span className={r.kind === "external" ? "text-sky-600" : "text-emerald-600"}>{r.kind === "external" ? "External" : "Internal"}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs font-semibold text-slate-700">{r.count} view{r.count === 1 ? "" : "s"}</div>
            <div className="text-[10px] text-slate-400">last {fmtLog(r.last_ts)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}


function IncidentsList({ name, canOpen }) {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.staffProfileIncidents(name).then(setRows).catch(() => setRows([])); }, [name]);
  if (rows === null) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (rows.length === 0) return <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No incidents reported.</div>;
  return (
    <div className="space-y-2" data-testid="incidents-list">
      {rows.map((r) => (
        <div key={r.name} onClick={canOpen ? () => navigate(`/incidents/${encodeURIComponent(r.name)}`) : undefined}
          className={`rounded-lg border border-slate-200 bg-white p-3 ${canOpen ? "cursor-pointer hover:border-rose-300 hover:bg-rose-50/40 transition-colors" : ""}`}
          data-testid={`incident-${r.name}`}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5">{r.participant_name || "—"}{canOpen && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}</span>
            <div className="flex items-center gap-2 shrink-0">
              {r.reportable ? <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[9px] py-0">Reportable</Badge> : null}
              <Badge className={`text-[9px] py-0 border ${SEV_TONE[r.severity] || SEV_TONE.Low}`}>{r.severity || "—"}</Badge>
              <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[9px] py-0">{r.status || "—"}</Badge>
            </div>
          </div>
          <div className="text-[11px] text-slate-400 mb-1">{fmtDT(r.incident_datetime)}{r.types?.length ? ` · ${r.types.join(", ")}` : ""}</div>
          <div className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-3">{r.description || <span className="text-slate-300">No description.</span>}</div>
        </div>
      ))}
    </div>
  );
}
